import { webview } from 'feature/webview/webview'
import { makeAutoObservable, runInAction, when } from 'mobx'
import { employee } from 'page/catalog/employee/store/EmployeeStore'
import { opt } from 'store/opt'
import { DemoCatalogParams } from 'type/demo'
import { Product } from 'type/Product'
import { StandWidget } from 'type/tryon'
import { skipAsync } from 'util/async'
import { omit } from 'util/object'
import { delay } from 'util/promise'
import { loadJson, saveJson } from 'util/storage'

export type UpdateEvent = {
  user_id?: string
  gender?: string
  picture?: string
  outfits?: number
  favorites?: number
  last_time?: number
}

export type UpdateListener = (event: UpdateEvent) => void

type Props = {
  params: DemoCatalogParams
  widget: StandWidget
  window: Product
}

function buildSrc(src: string) {
  const debug = localStorage.getItem('debug_widget_js')
  if (debug) return debug
  try {
    const url = new URL(src)
    if (opt.sizing) url.searchParams.append('sizing', '')
    return url.toString()
  } catch {
    return src
  }
}

class TryOnStore {
  private _init = false
  private _ready = false
  private _error = false
  private _user = ''
  private props: Props = null!
  currentItemId: string = ''

  constructor() {
    makeAutoObservable(this)
  }

  get ready(): boolean {
    return this._ready
  }

  get error(): boolean {
    return this._error
  }

  get user(): string {
    return this._user
  }

  readonly onUpdate = (listener: UpdateListener) => {
    when(() => this.ready, () => {
      if (!window.showoff) return
      window.showoff.on('update', listener, { immediate: true })
    })
  }

  readonly open = skipAsync(async (item?: string, combo_item_id?: string) => {
    await when(() => this.ready)
    if (!window.showoff) return
    this.currentItemId = item ?? ''
    window.showoff.open({ item, combo_item_id, autostart: true })
  })

  readonly boutique = skipAsync(async (user: string | undefined, manager: string | undefined) => {
    await when(() => this.ready)
    if (!window.showoff) return
    const test = this.props.window.test || employee.user?.test || this.props.params.test || undefined
    const options = omit({ ...window.showoff.options, test, boutique: true, external: true, user, manager })
    window.showoff.setup(options)
  })

  readonly fav = skipAsync(async () => {
    await when(() => this.ready)
    if (!window.showoff) return
    window.showoff.open({ favorites: true })
  })

  readonly openProfile = skipAsync(async () => {
    await when(() => this.ready)
    if (!window.showoff) return
    window.showoff.open({ profile: true })
  })

  readonly close = () => {
    if (this.ready && window.showoff) window.showoff.close()
  }

  readonly exit = () => {
    this.user = ''
    if (this.ready && window.showoff) {
      window.showoff.close()
      window.showoff.exit()
    }
  }

  async destroy() {
    this._init = false
    window.postMessage({ name: 'showoff-sizing-kill' })
    if (window.showoff?.destroy) {
      window.showoff.destroy({ exit: true, kill: true })
      await delay(1000)
    }
  }

  init(props: Props) {
    if (this._init) return
    try {
      this._init = true
      this._ready = false
      this._error = false
      this.user = props.params.stand ? loadJson('outfits.user_id') ?? '' : ''
      this.props = props
      void this.create()
    } catch (e) {
      console.error('init failed', e)
      this._error = true
    }
  }

  private set user(value: string) {
    saveJson('outfits.user_id', value || undefined)
    this._user = value
  }

  private async create() {
    const { basic, stand, turn, test } = this.props.params
    const { src, product, client, company, access } = this.props.widget
    const { product_id, try_on_combo_only, group } = this.props.window

    if (!src) throw new Error('no src')

    const lang = this.props.window.lang
    const user = this.user
    const external = user ? true : undefined
    const camera = stand
    const w = { product_id, try_on_combo_only, group }
    const wv = webview.detected || undefined
    const options = omit({
      product, client, company, lang, user, external, access,
      test, stand, basic, camera, turn, window: w, webview: wv,
    })
    window.showoff = { options } as any

    const element = document.createElement('script')
    element.src = buildSrc(src)
    element.async = true
    document.head.appendChild(element)

    while (!window.showoff?.on) {
      await delay(100)
    }

    runInAction(() => this._ready = true)
  }
}

export const try_on = new TryOnStore()
